// App-wide toast notifications.
//
// Rendered in the normal React tree by <ToastHost/> (mounted once in App), in
// the same `ui-toast` style ProPlan Connect and Pursuit Connect use. This
// deliberately replaces rsuite's `toaster`, whose imperative
// createRoot/findDOMNode path is broken under React 19. Call showToast() from
// anywhere — event handlers, effects, plain functions — without needing a hook
// or context.

// How long a toast spends sliding back out before it is taken off the list.
// Dropping it the moment it is dismissed would make it vanish rather than
// leave, so a dismissed toast is flagged first and removed when the slide has
// run. Kept in step with the exit animation in ToastHost.css.
const EXIT_ANIMATION_MS = 300;

let toasts = [];
const listeners = new Set();
let nextId = 1;

const emit = () => {
  for (const listener of listeners) listener();
};

export const subscribeToasts = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Returns a stable reference between changes so useSyncExternalStore is happy.
export const getToastsSnapshot = () => toasts;

export const dismissToast = (id) => {
  const target = toasts.find((t) => t.id === id);
  if (!target || target.leaving) return;

  toasts = toasts.map((t) => (t.id === id ? { ...t, leaving: true } : t));
  emit();

  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, EXIT_ANIMATION_MS);
};

// showToast(type, content, { header, duration })
//   type:    'success' | 'error' | 'warning' | 'info'
//   content: string or React node
//   header:  optional heading text (defaults per type)
//   duration ms before auto-dismiss; 0 keeps it until dismissed. Default 4500.
export const showToast = (type, content, options = {}) => {
  const id = nextId++;
  toasts = [
    ...toasts,
    {
      id,
      type: type || 'info',
      content,
      header: options.header ?? null,
      duration: options.duration == null ? 4500 : options.duration,
    },
  ];
  emit();
  return id;
};
